'use client';

import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import 'dayjs/locale/en';
import { useTranslations } from 'next-intl';
import { getCurrentLocale } from './route';

/** 브라우저 타임존 (모듈 레벨 캐시) */
let cachedTimeZone: string | undefined;
function getUserTimeZone(): string {
  if (!cachedTimeZone) {
    cachedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return cachedTimeZone;
}

/**
 * 24시간 미만이면 시간(오전/오후 HH:MM), 이상이면 YY.MM.DD 형식으로 포맷합니다.
 * MySpace 카드 컴포넌트들에서 공통으로 사용하는 상대적 날짜 포맷입니다.
 *
 * @param dateString - ISO 형식의 날짜 문자열
 * @param formatter - next-intl의 useFormatter()가 반환하는 formatter
 */
export function formatRelativeCardDate(
  dateString: string,
  formatter: {
    dateTime: (
      date: Date | number,
      options?: Record<string, unknown>,
    ) => string;
  },
): string {
  const date = dayjs(dateString);
  const diffInHours = dayjs().diff(date, 'hour');

  if (diffInHours < 24) {
    return formatter.dateTime(date.toDate(), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: getUserTimeZone(),
    });
  }

  const localDate = new Date(dateString);
  const localYear = localDate.getFullYear();
  const localMonth = String(localDate.getMonth() + 1).padStart(2, '0');
  const localDay = String(localDate.getDate()).padStart(2, '0');

  // en: 미국식 MM/DD/YYYY
  if (getCurrentLocale() === 'en') {
    return `${localMonth}/${localDay}/${localYear}`;
  }

  // ko(기본): YY.MM.DD
  return `${String(localYear).slice(-2)}.${localMonth}.${localDay}`;
}

/**
 * 브라우저의 언어 설정을 감지하여 로케일을 가져옵니다.
 * 서버사이드에서는 기본값 'ko'를 반환합니다.
 *
 * @returns {string} 감지된 로케일 ('ko' 또는 'en' 등), 기본값: 'ko'
 *
 * @example
 * ```typescript
 * const locale = detectBrowserLocale();
 * console.log(locale); // 'ko' 또는 'en'
 * ```
 */
export const detectBrowserLocale = (): string => {
  // 서버사이드에서는 기본값 사용
  if (
    typeof window === 'undefined' ||
    typeof navigator === 'undefined' ||
    !navigator.language
  ) {
    return 'ko';
  }

  try {
    // 브라우저 언어 가져오기 (ko-KR 형태를 ko로 변환)
    return navigator.language.split('-')[0] || 'ko';
  } catch {
    return 'ko';
  }
};

/**
 * 날짜 문자열을 한국어 형식으로 포맷합니다.
 * "YY.MM.DD 오전/오후 HH:MM 생성됨" 또는 "YY.MM.DD 오전/오후 HH:MM 수정됨" 형태로 변환합니다.
 *
 * @param {string} dateString - UTC 형식의 날짜 문자열 (예: "2025-05-23T03:24:44Z")
 * @param {'created' | 'updated'} type - 날짜 타입 ('created': 생성됨, 'updated': 수정됨)
 * @returns {string} 포맷된 날짜 문자열
 *
 * @example
 * ```typescript
 * const createdDate = formatOutputDate("2025-05-23T03:24:44Z", "created");
 * console.log(createdDate); // "25.05.23 오전 03:24 생성됨"
 *
 * const updatedDate = formatOutputDate("2025-05-23T20:03:15Z", "updated");
 * console.log(updatedDate); // "25.05.23 오후 08:03 수정됨"
 * ```
 *
 * @throws {Error} 유효하지 않은 날짜 문자열이 전달된 경우
 */
export const formatOutputDate = (
  dateString: string,
  type: 'created' | 'updated' = 'created',
  t: ReturnType<typeof useTranslations>,
  showSuffix: boolean = true,
): string => {
  // 현재 UI 로케일 (URL 기준) 감지 — 전역 dayjs.locale() 변형 대신 인스턴스 단위로 주입해
  // SSR/client 및 다른 dayjs 소비자와의 전역 side effect·race 를 방지한다.
  const locale = getCurrentLocale();

  // 유효성 검사
  if (!dateString) {
    throw new Error('날짜 문자열이 필요합니다.');
  }

  // UTC 시간을 로컬 시간으로 변환 (로케일 인스턴스 주입)
  const date = dayjs(dateString).locale(locale);

  if (!date.isValid()) {
    throw new Error('유효하지 않은 날짜 형식입니다.');
  }

  // 타입에 따른 접미사
  const suffix = type === 'created' ? t('Date.created') : t('Date.updated');

  // en: 미국식 MM/DD/YYYY h:mm A (예: "05/23/2025 3:24 AM")
  if (locale === 'en') {
    const enFormat = date.format('MM/DD/YYYY h:mm A');
    return `${enFormat}${showSuffix ? suffix : ''}`;
  }

  // ko(기본): YY.MM.DD 오전/오후 hh:mm
  const dateFormat = date.format('YY.MM.DD');
  const period = date.hour() < 12 ? t('Date.AM') : t('Date.PM');
  const hour12 = date.format('hh:mm');

  return `${dateFormat} ${period} ${hour12}${showSuffix ? suffix : ''}`;
};

/**
 * 객체의 createdAt 또는 updatedAt 필드를 사용하여 날짜를 포맷합니다.
 * createdAt과 updatedAt이 모두 있는 경우, updatedAt을 우선적으로 사용합니다.
 *
 * @param {Object} obj - 날짜 필드를 포함한 객체
 * @param {string} [obj.createdAt] - 생성일 (UTC 형식)
 * @param {string} [obj.updatedAt] - 수정일 (UTC 형식)
 * @returns {string} 포맷된 날짜 문자열, 날짜가 없으면 빈 문자열
 *
 * @example
 * ```typescript
 * const output = {
 *   id: 182,
 *   name: "무한상사는 십분도 순삭 가능😎",
 *   createdAt: "2025-05-23T03:24:44Z",
 *   updatedAt: "2025-05-23T03:27:33Z"
 * };
 *
 * const formattedDate = formatOutputObjectDate(output);
 * console.log(formattedDate); // "25.05.23 오전 03:27 수정됨"
 *
 * // createdAt만 있는 경우
 * const output2 = {
 *   id: 183,
 *   createdAt: "2025-05-23T15:30:00Z"
 * };
 *
 * const formattedDate2 = formatOutputObjectDate(output2);
 * console.log(formattedDate2); // "25.05.23 오후 03:30 생성됨"
 * ```
 */
export const formatOutputObjectDate = (obj: {
  createdAt?: string;
  updatedAt?: string;
  t: ReturnType<typeof useTranslations>;
}): string => {
  const { createdAt, updatedAt, t } = obj;

  // 날짜가 없으면 빈 문자열 반환
  if (!createdAt && !updatedAt) {
    return '';
  }

  // updatedAt이 있고 createdAt과 다르면 updatedAt 사용
  if (updatedAt && createdAt && updatedAt !== createdAt) {
    return formatOutputDate(updatedAt, 'updated', t);
  }

  // createdAt 사용 (updatedAt이 없거나 createdAt과 같은 경우)
  if (createdAt) {
    return formatOutputDate(createdAt, 'created', t);
  }

  // updatedAt만 있는 경우
  if (updatedAt) {
    return formatOutputDate(updatedAt, 'updated', t);
  }

  return '';
};

/**
 * 초 단위 시간을 HH:MM:SS, MM:SS, 또는 00:SS 형식의 문자열로 변환합니다.
 *
 * - 시간이 1시간 이상일 경우: HH:MM:SS (예: 3675.5초 -> "01:01:15")
 * - 시간이 1시간 미만이고 1분 이상일 경우: MM:SS (예: 75.2초 -> "01:15")
 * - 시간이 1분 미만일 경우: 00:SS (예: 45초 -> "00:45")
 * - 입력 값이 0일 경우: "00:00"
 *
 * @param {number} totalSeconds - 변환할 총 초 (소수점 포함 가능, 내부적으로 정수 처리).
 * @returns {string} 포맷된 시간 문자열.
 *
 * @example
 * ```typescript
 * console.log(formatSecondsToTimeString(3675.5)); // "01:01:15"
 * console.log(formatSecondsToTimeString(75.2));    // "01:15"
 * console.log(formatSecondsToTimeString(45));       // "00:45"
 * console.log(formatSecondsToTimeString(0));        // "00:00"
 * console.log(formatSecondsToTimeString(59.9));     // "00:59"
 * console.log(formatSecondsToTimeString(60));       // "01:00"
 * console.log(formatSecondsToTimeString(3599));     // "59:59"
 * console.log(formatSecondsToTimeString(3600));     // "01:00:00"
 * ```
 */
export const formatSecondsToTimeString = (totalSeconds: number): string => {
  const totalSecondsFloored = Math.floor(totalSeconds);

  const hours = Math.floor(totalSecondsFloored / 3600);
  const minutes = Math.floor((totalSecondsFloored % 3600) / 60);
  const seconds = totalSecondsFloored % 60;

  const paddedSeconds = seconds.toString().padStart(2, '0');
  const paddedMinutes = minutes.toString().padStart(2, '0');

  if (hours > 0) {
    const paddedHours = hours.toString().padStart(2, '0');
    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
  }
  if (minutes > 0) {
    return `${paddedMinutes}:${paddedSeconds}`;
  }
  return `00:${paddedSeconds}`;
};

/**
 * 날짜 문자열을 짧은 형식으로 포맷합니다.
 * "YY.MM.DD 오전/오후 HH:MM" 형태로 변환합니다.
 * 사용자의 로케일에 따라 오전/오후 표시가 다국어 처리됩니다.
 *
 * @param {string} dateString - UTC 형식의 날짜 문자열 (예: "2025-05-23T03:24:44Z")
 * @param {ReturnType<typeof useTranslations>} t - 번역 함수
 * @returns {string} 포맷된 날짜 문자열
 *
 * @example
 * ```typescript
 * const date = formatShortDateTime("2025-05-23T03:24:44Z", t);
 * console.log(date); // "25.05.23 오전 03:24" (한국어)
 *
 * const date2 = formatShortDateTime("2025-05-23T20:03:15Z", t);
 * console.log(date2); // "25.05.23 오후 08:03" (한국어)
 * ```
 *
 * @throws {Error} 유효하지 않은 날짜 문자열이 전달된 경우
 */
export const formatShortDateTime = (
  dateString: string,
  t: ReturnType<typeof useTranslations>,
): string => {
  // 현재 UI 로케일 (URL 기준) 감지 — 전역 dayjs.locale() 변형 대신 인스턴스 단위로 주입해
  // SSR/client 및 다른 dayjs 소비자와의 전역 side effect·race 를 방지한다.
  const locale = getCurrentLocale();

  // 유효성 검사
  if (!dateString) {
    throw new Error('날짜 문자열이 필요합니다.');
  }

  // UTC 시간을 로컬 시간으로 변환 (로케일 인스턴스 주입)
  const date = dayjs(dateString).locale(locale);

  if (!date.isValid()) {
    throw new Error('유효하지 않은 날짜 형식입니다.');
  }

  // en: 미국식 MM/DD/YYYY h:mm A
  if (locale === 'en') {
    return date.format('MM/DD/YYYY h:mm A');
  }

  // ko(기본): YY.MM.DD 오전/오후 hh:mm
  const dateFormat = date.format('YY.MM.DD');
  const period = date.hour() < 12 ? t('Date.AM') : t('Date.PM');
  const hour12 = date.format('hh:mm');

  return `${dateFormat} ${period} ${hour12}`;
};
